import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/base-components';
import { cx, FOCUS_RING } from '../components/styles';
import { ANCHORS, CONTAINER, SECTION_PADDING, TRIAL_DAYS } from './landingConfig';
import { Reveal } from './Reveal';
import { useLandingCtas } from './useLandingCtas';

/**
 * Section 8 — plans and prices (DESIGN_PLAN §1.8).
 *
 * PRICES ARE HARDCODED HERE, deliberately. This page makes no API calls, and
 * that rule stands — do NOT solve drift by adding a fetch. The numbers below
 * mirror `TIER_DEFS` in oneClickTixCore
 * `flashtools/management/commands/setup_flashtools_stripe.py`, and the trial
 * terms mirror `flashtools/services/billing.py`
 * (TRIAL_PERIOD_DAYS / TRIAL_ELIGIBLE_TIERS / TRIAL_METER_CAPS / METER_UNIT_CENTS).
 * If a price changes in Stripe, change it here in the same commit. `/billing`
 * reads the live API and stays the source of truth for anyone signed in.
 *
 * WHY PRICES ARE HERE AT ALL: visitors reached the end of the funnel without
 * ever seeing a number, started the trial, and then had no idea what they'd be
 * charged or where to pay when it ended. The `afterTrial` line on each card
 * answers that on the page, before signup.
 *
 * Reserve is deliberately absent. It is invite-only, so advertising it to the
 * public — even as "invite only" — creates demand for something a visitor
 * cannot buy, and exposes the drop bot that tier carries. The upgrade path
 * appears in-app on `/billing` only for accounts holding the invite flag.
 *
 * CTAs come from `useLandingCtas`, so a signed-in visitor is sent into the app
 * rather than to a login screen they do not need. Signed-out goes to `/login`,
 * which renders both sign-in and sign-up. Never link to `/billing`: it is behind
 * auth and an unsubscribed visitor would be bounced.
 */
interface LandingPlan {
  name: string;
  monthly: string;
  annual: string;
  line: string;
  /** What happens at the end of the trial, or when the first invoice lands. */
  afterTrial: string;
  features: string[];
  /**
   * Costs on top of the flat price. Rendered with a `+` and an emphasised
   * figure, never a check — too many sellers read the per-signup rate as part
   * of the plan and met it on an invoice instead.
   */
  addOns: { price: string; label: string }[];
  highlighted: boolean;
  badge: string | null;
}

const PLANS: LandingPlan[] = [
  {
    name: 'Core',
    monthly: '$30',
    annual: '$300/year — two months free',
    line: 'Run every account you own from one place.',
    afterTrial: `We take your card at signup. After ${TRIAL_DAYS} days Core bills $30/month automatically — cancel before then and you are not charged.`,
    features: [
      'DICE, AXS and TAO ticket sync',
      'CrowdVolt listings, transfers and gifting',
      'Drop and price alerts',
      'Every account in one inventory',
    ],
    addOns: [],
    highlighted: false,
    badge: null,
  },
  {
    name: 'Pro',
    monthly: '$50',
    annual: '$500/year — two months free',
    // Seller Waitlists is Pro's differentiator and the reason to upgrade, so it
    // leads both the one-liner and the feature list. Core does not carry the
    // feature at all — a Core account opening Waitlists gets the upgrade gate.
    line: 'Core plus Seller Waitlists — the tier that can join waitlists.',
    afterTrial: 'No trial on Pro — it bills $50 on the first invoice. Start on Core and upgrade under Billing whenever you want waitlists.',
    features: [
      'Seller Waitlists — sold-out shows and waitlists for hot cities',
      'Everything in Core',
      '100 waitlist signups included every month',
    ],
    addOns: [{ price: '$0.10', label: 'per waitlist signup past the first 100, billed monthly' }],
    highlighted: true,
    badge: 'Most popular',
  },
];

export const PlanTeaser: React.FC = () => {
  const { primary, signedIn } = useLandingCtas();
  return (
    <Reveal as="section" id={ANCHORS.plans} className={SECTION_PADDING}>
      <div className={CONTAINER}>
        <h2 className="text-3xl font-bold tracking-tight text-white">Two plans.</h2>
        <p className="mt-3 text-white/60">
          Start on Core. Move up when you need to. Prices in USD, billed through Stripe.
        </p>

        <div className="mt-10 grid gap-4 md:grid-cols-2">
          {PLANS.map((plan) => (
            <div
              key={plan.name}
              className={cx(
                'flex flex-col rounded-2xl border bg-white/[0.02] p-6',
                plan.highlighted ? 'border-[#3b82f6]/40' : 'border-white/[0.06]',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-lg font-semibold text-white">{plan.name}</p>
                {plan.badge && (
                  <span className="rounded-full border border-[#3b82f6]/25 bg-[#3b82f6]/15 px-2 py-0.5 text-[11px] text-[#60a5fa]">
                    {plan.badge}
                  </span>
                )}
              </div>

              <div className="mt-4 flex items-baseline gap-1.5">
                <span className="text-4xl font-bold tracking-tight text-white">{plan.monthly}</span>
                <span className="text-sm text-white/45">/month</span>
              </div>
              <p className="mt-1 text-xs text-white/40">{plan.annual}</p>

              <p className="mt-4 text-sm leading-relaxed text-white/60">{plan.line}</p>

              <div className="mt-5 border-t border-white/[0.06]" />

              <ul className="mt-5 flex-1 space-y-2.5 text-sm">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex gap-2.5 text-white/70">
                    <span aria-hidden="true" className="mt-0.5 text-[#3b82f6]">✓</span>
                    <span>{feature}</span>
                  </li>
                ))}
                {/* Pay-per-use is a cost, not a perk — a `+`, never a check, and
                    the figure in amber so it cannot be skimmed past. */}
                {plan.addOns.map((addOn) => (
                  <li key={addOn.label} className="flex gap-2.5 text-white/60">
                    <span aria-hidden="true" className="mt-0.5 text-amber-300">+</span>
                    <span>
                      <strong className="font-semibold text-amber-300">{addOn.price}</strong>{' '}
                      {addOn.label}
                    </span>
                  </li>
                ))}
              </ul>

              <p className="mt-5 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2.5 text-xs leading-relaxed text-white/45">
                {plan.afterTrial}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-start gap-3">
          <Link to={primary.to} className={cx('rounded-lg', FOCUS_RING)}>
            <Button size="lg" whitespace="nowrap">
              {primary.label}
            </Button>
          </Link>
          {!signedIn && (
            <p className="text-xs text-white/40">
              Sign up, pick Core or Pro, and pay through Stripe checkout. Change or cancel your plan
              any time under Billing.
            </p>
          )}
        </div>
      </div>
    </Reveal>
  );
};
